import { Button, Message, Text, Theme, useIsTheme, usePhoneNumber } from '@afterpaytouch/core'
import { Trans, useTranslation } from 'next-i18next'
import React, { FunctionComponent, useEffect, useMemo, useRef, useState } from 'react'
import { Country } from '@afterpaytouch/portal-api/types'
import { Route } from '../../config/router'
import { isValidObject } from '@afterpaytouch/form/lib/utils/object'
import { FieldAnalyticsLabels, SignupFormAnalyticsFields } from './constants'
import { Disclaimer } from '../Disclaimer/Disclaimer'
import { useSignUpTerms } from '../Disclaimer/hooks'
import FormBuilder from '@afterpaytouch/form'
import { getSchema } from './utils'

import { compareObjects } from '../../utils/comparison'
import { handleAccountDetailsErrors } from '../ProfileForm/helper'
import { useRouter } from 'next/router'
import { LaunchDarklyFlags, useFlag } from '../../hooks'
import { useFlag as useFlagFeature } from '@afterpaytouch/feature'
import { SupportedLocale } from '@afterpaytouch/shop-api'
import { CreateAccountRequest, registrationErrors } from '../../model/registration'
import {
  useConsumerContactAddress,
  useConsumerEmail,
  useConsumerGivenName,
  useConsumerMobile,
  useConsumerName,
  useConsumerSelector,
  useConsumerSurname,
  useFinaliseAccountDetailsMutation,
} from '../../state'
import { ProfileCard } from '../ProfileCard'
import { ConsumerAddressType } from '@afterpay/types'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { TrackingEvent } from '../../model/amplitude'
import { valueIsNotNullOrUndefined } from '@afterpay/utils'
import { getCountryCodeFromLocale } from '../../utils/consumer'
import { MINIMUM_AGE_RESTRICTION } from '../VerifyChallenge/helper'
import { isCountryNeedsSoftIdAttempt } from '../../model'
import { useIdentityVerifyRedirect } from '../../hooks/useIdentityVerifyRedirect/useIdentityVerifyRedirect'
import { useIdFlowErrorHandler } from '../IDFlow'
import { SignupFormData } from './types'

export const getEnteredPrefillEvent = (attr: string): string => `Entered Profile Confirm Page ${attr} Field`

interface EnableDobInfoFlag {
  variation?: 'A' | 'B' | 'C' | 'D'
  values: string[]
}

export const getRequest = (
  data: SignupFormData,
  initialFormData: SignupFormData,
  preferredLocale = 'en-AU' as SupportedLocale,
  creditCheckAgree: boolean
): CreateAccountRequest => {
  const { givenNames, otherGivenNames, surname, dateOfBirth, mobile, email, address, marketingOptIn } = data
  return {
    ...address,
    dateOfBirth,
    givenNames: givenNames ?? initialFormData.givenNames,
    mobile,
    email: email ?? initialFormData.email,
    otherGivenNames: otherGivenNames ?? initialFormData.otherGivenNames,
    surname: surname ?? initialFormData.surname,
    preferredLocale,
    creditCheckAgree,
    marketingOptIn,
  }
}

export const SignupForm: FunctionComponent = (): JSX.Element => {
  const signUpTerms = useSignUpTerms()
  const { t } = useTranslation()
  const givenNames = useConsumerGivenName() ?? ''
  const surname = useConsumerSurname() ?? ''
  const phoneNumber = useConsumerMobile()
  const supportedCountries = [Country.AU]
  const { locale, push } = useRouter()
  const country = getCountryCodeFromLocale(locale)
  const { parseMobile } = usePhoneNumber({ country })
  const consumer = useConsumerSelector()
  const [registrationError, setRegistrationError] = useState<string | null>(null)
  const enableEditName = true
  const isMobileLogin = false
  const [isSubmitted, setIsSubmitted] = useState(false)
  const consumerContactAddress = useConsumerContactAddress()
  const initialAddresses = {
    postcode: consumerContactAddress?.postcode ?? '',
    state: consumerContactAddress?.state ?? '',
    suburb: consumerContactAddress?.suburb ?? '',
    area2: consumerContactAddress?.area2 ?? '',
    id: consumerContactAddress?.id ?? 0,
    name: consumerContactAddress?.name ?? '',
    address1: consumerContactAddress?.address1 ?? '',
    countryCode: consumerContactAddress?.countryCode ?? Country.AU,
    addressType: consumerContactAddress?.addressType ?? ConsumerAddressType.CONSUMER_ACCOUNT_CONTACT_ADDRESS,
    address2: consumerContactAddress?.address2 ?? '',
  }
  const consumerName = useConsumerName()
  const email = useConsumerEmail() ?? ''
  const [finaliseAccountDetails] = useFinaliseAccountDetailsMutation()
  const isStateEligibleForPayByInstallmentEnabled = useFlag(LaunchDarklyFlags.REGISTRATION_STATE_VALIDATION)
  const isSoftIdVerificationEnabled = useFlag(LaunchDarklyFlags.SOFT_ID_VERIFICATION_CHECK_ENABLED)
  const isUKMarketRegsFCA = useFlag(LaunchDarklyFlags.UK_MRKT_REGS_FCA)
  const dobInfoFlagEnabledCountries = useFlagFeature<EnableDobInfoFlag>(LaunchDarklyFlags.ENABLE_DOB_INFO, { values: [] })
  const showDobInfo = dobInfoFlagEnabledCountries.values.includes(country)
  // const shouldVerify = consumer.smsVerificationEnabled

  const defaultMobile = parseMobile(consumer?.phoneNumber ?? phoneNumber) ?? ''
  const isCashTheme = useIsTheme(Theme.Cash)
  const [isAddressPrefilled, setIsAddressPrefilled] = useState<boolean | null>(null)
  const countryMinimumAgeRestriction = MINIMUM_AGE_RESTRICTION[country] ?? MINIMUM_AGE_RESTRICTION.DEFAULT_COUNTRY
  const addressState = Array.isArray(initialAddresses) ? initialAddresses[0]?.state : initialAddresses?.state
  const initialMinAge = countryMinimumAgeRestriction[addressState ?? ''] ?? countryMinimumAgeRestriction.DEFAULT
  const [minAge, setMinAge] = useState<number>(initialMinAge)
  const isNzCreditCheckEnabled = country === Country.NZ
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const { handleError: handleIdFlowError } = useIdFlowErrorHandler()
  const { verifyAndHandleOutcome } = useIdentityVerifyRedirect()
  const [hasSentAnalyticsData, setHasSentAnalyticsData] = useState({
    givenNames: false,
    otherGivenNames: false,
    surname: false,
    dateOfBirth: false,
    mobile: false,
    address: false,
  })

  const { schema, initialFormData } = useMemo(() => {
    const components = {
      terms: () => <Disclaimer />,
      continueButton: () => {
        return (
          <Button.Primary
            onClick={() => {
              logEvent(TrackingEvent.PRESSED_CONTINUE_ON_PROFILE_CONFIRM_PAGE)
            }}
            loading={isSubmitted}
            type='submit'
            testNameSpace='sign-up-submit'
            disabled={isSubmitted}
            padding='Fluid'
          >
            {t('common:continue')}
          </Button.Primary>
        )
      },

      inlineError: () => {
        return (
          <Text color='Fire' size='XS' testNameSpace='sign-up-error'>
            <Trans i18nKey={`signUp:error.${registrationError as string}`} />
          </Text>
        )
      },
    }
    const handleGeocodeResult = (success: boolean): void => {
      setIsAddressPrefilled(success)
    }
    return getSchema({
      components,
      t,
      givenNames,
      surname,
      enableEditName,
      consumer,
      locale: (locale ?? 'en-AU') as SupportedLocale,
      country,
      minAge,
      defaultMobile,
      isMobileLogin,
      parseMobile,
      supportedPrefixes: supportedCountries,
      initialAddresses,
      isStateEligibleForPayByInstallmentEnabled,
      handleGeocodeResult,
      showDobInfo,
      showInlineError: registrationError !== null,
      logEvent,
      isCashTheme,
    })
  }, [
    enableEditName,
    consumer,
    country,
    minAge,
    defaultMobile,
    initialAddresses,
    isStateEligibleForPayByInstallmentEnabled,
    showDobInfo,
    enableEditName,
    isAddressPrefilled,
    givenNames,
    isNzCreditCheckEnabled,
    isSubmitted,
    surname,
    minAge,
    registrationError,
  ])

  const [formData, setFormData] = useState(initialFormData)
  const preFilledInputNames = useRef(isValidObject(initialFormData) ? Object.keys(initialFormData).filter((key) => initialFormData[key]?.length > 0) : [])
  const creditCheckAgree = (consumer?.countryCode ?? country) !== Country.CA

  const handleSuccess = async (): Promise<void> => {
    push(Route.LOGGED_IN)
  }
  const doCreateAccount = async (data: SignupFormData): Promise<void> => {
    const request: CreateAccountRequest = getRequest(data, initialFormData, (locale ?? 'en-AU') as SupportedLocale, creditCheckAgree)
    try {
      // TODO: Navigate to sms delay page
      // if (shouldVerify) {
      // }
      await finaliseAccountDetails({
        ...request,
        countryCode: consumer?.countryCode,
        creditPolicyAgree: false,
        providedCreditCheckConsent: isNzCreditCheckEnabled,
      }).unwrap()

      if (isCountryNeedsSoftIdAttempt(consumer?.countryCode)) {
        logEvent(TrackingEvent.IDENTITY_VERIFY_FIRST_ATTEMPT)
        await verifyAndHandleOutcome({ showToastBody: true })
      } else {
        handleSuccess()
      }
    } catch (error) {
      const errorCode = error.data?.errorCode ?? 'default'
      setRegistrationError(registrationErrors[errorCode])
      handleAccountDetailsErrors(error, isSoftIdVerificationEnabled, handleIdFlowError)
    } finally {
      setIsSubmitted(false)
    }
  }

  const handleOnChange = (data: SignupFormData): void => {
    setRegistrationError(null)
    if (!compareObjects(data.address, formData.address, false, [], true)) {
      const updatedMinAge = countryMinimumAgeRestriction[data.address.state ?? ''] ?? countryMinimumAgeRestriction.DEFAULT
      setMinAge(updatedMinAge)
    }
    setFormData(data)
    SignupFormAnalyticsFields.forEach((key) => {
      if (!(isMobileLogin && key === 'email')) {
        if (valueIsNotNullOrUndefined(data[key]) && valueIsNotNullOrUndefined(formData[key]) && data[key] !== formData[key] && !hasSentAnalyticsData[key]) {
          const wasPrefilled = preFilledInputNames.current.includes(key)
          // @ts-ignore
          logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels[key]), { wasPrefilled: !!wasPrefilled })
          setHasSentAnalyticsData({ ...hasSentAnalyticsData, [key]: true })
        }
      }
      if (isAddressPrefilled !== null) {
        if (!compareObjects(data.address, formData.address, false, [], true) && !hasSentAnalyticsData.address) {
          const wasPrefilled = preFilledInputNames.current.includes('address')
          // @ts-ignore
          logEvent(getEnteredPrefillEvent(FieldAnalyticsLabels.address), { wasPrefilled: !!wasPrefilled })
          setHasSentAnalyticsData({ ...hasSentAnalyticsData, address: true })
        }
      }
    })
  }

  const handleOnSubmit = async (data: SignupFormData): Promise<void> => {
    setIsSubmitted(true)
    await doCreateAccount(data)
  }

  const handleStateChange = (isValid: boolean, errors): void => {
    if (!isValid) {
      setIsSubmitted(false)
    }
  }

  const renderDisclaimer = (): JSX.Element => {
    const disclaimer = isUKMarketRegsFCA ? 'signUp:disclaimerFCA' : 'signUp:disclaimer'
    return (
      <Text size='XS'>
        <Message iconName='Info' description={<Trans i18nKey={disclaimer} components={signUpTerms} />} />
      </Text>
    )
  }

  useEffect(() => {
    if (isAddressPrefilled !== null && !preFilledInputNames.current.includes('address')) {
      preFilledInputNames.current = [...preFilledInputNames.current, ...(isAddressPrefilled ? ['address'] : [])]
    }
  }, [isAddressPrefilled])

  return (
    <>
      <div className='mb-4 mt-4'>
        <ProfileCard consumerName={consumerName} consumerEmail={email ?? ''} allowLogout />
      </div>
      <FormBuilder schema={schema} testNameSpace={'sign-up'} onSubmit={handleOnSubmit} onChange={handleOnChange} onStateChange={handleStateChange} />
      {country === Country.GB && renderDisclaimer()}
    </>
  )
}
